import React, { useState, useRef, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { HealthcareChatbotEngine } from '../utils/healthcareChatbot';
import './HealthcareChatbot.css';

const HealthcareChatbot = () => {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const chatbotEngine = useRef(new HealthcareChatbotEngine());

  useEffect(() => {
    // Initial welcome message
    if (messages.length === 0) {
      const welcomeMessage = {
        id: uuidv4(),
        text: "👋 Hello! I'm your Healthcare Assistant. I can help answer general health questions, provide wellness tips, and information about common symptoms. How can I assist you today?",
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages([welcomeMessage]);
    }
  }, [messages.length]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage = {
      id: uuidv4(),
      text: inputMessage,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    // Simulate thinking time for better UX
    setTimeout(() => {
      const botResponse = chatbotEngine.current.processMessage(inputMessage);
      const botMessage = {
        id: uuidv4(),
        text: botResponse,
        sender: 'bot',
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);
      setIsLoading(false);
    }, 1000);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const clearChat = () => {
    chatbotEngine.current.clearHistory();
    setMessages([]);
  };

  const toggleChat = () => {
    setIsOpen(!isOpen);
  };

  const formatMessage = (text) => {
    // Split by newlines and create paragraphs
    return text.split('\n').map((paragraph, index) => {
      if (paragraph.trim() === '') return null;
      return (
        <div key={index} className="message-paragraph">
          {paragraph}
        </div>
      );
    }).filter(Boolean);
  };

  const quickSuggestions = [
    // General Health & Wellness
    "What are tips for better sleep?",
    "How much water should I drink daily?",
    "What is a balanced diet?",
    "How can I reduce stress?",
    "What are good hygiene practices?",
    // Common Symptoms
    "I have a fever, what should I do?",
    "How do I treat a sore throat?",
    "What causes a runny nose?",
    "How can I get rid of a headache?",
    "What should I do for a rash?",
    // Chronic & Mixed Conditions
    "What is diabetes?",
    "How to manage high blood pressure?",
    "What are the symptoms of arthritis?",
    "How to prevent asthma attacks?",
    "What are allergy symptoms?",
    // Severe & Emergency Situations
    "What should I do for chest pain?",
    "How do I help someone who is having a seizure?",
    "What to do if someone faints?",
    "How to treat a severe burn?",
    "When should I call 911?"
  ];

  const moreExamples = [
    // First Aid & Injuries
    "How do I treat a minor burn?",
    "What to do for a cut or wound?",
    "How to stop severe bleeding?",
    "What is the first aid for a broken bone?",
    // Special Populations
    "What are common child health concerns?",
    "How to care for elderly parents?",
    "What are important pregnancy tips?",
    "What to do if my child has a fever?",
    // Mental Health
    "How to manage anxiety?",
    "What are signs of depression?",
    "How to improve mental health?",
    // Preventive Care
    "What vaccines do I need?",
    "How often should I get a checkup?",
    "What are important health screenings?",
    // Natural Language
    "I feel dizzy and nauseous, what should I do?",
    "My child has a rash and fever, is it serious?",
    "What are the warning signs of dehydration?",
    "How do I help someone who is unconscious?"
  ];

  const helpfulHints = [
    "💡 Try: 'I have [symptom]' - e.g., 'I have chest pain'",
    "💡 Ask: 'What is [disease]?' - e.g., 'What is diabetes?'",
    "💡 Type: '[Disease] symptoms' - e.g., 'asthma symptoms'",
    "💡 Say: 'How to treat [condition]?' - e.g., 'How to treat anxiety?'",
    "💡 Ask: 'Prevention for [disease]' - e.g., 'prevention for heart disease'",
    "💡 Type: '[Body part] pain' - e.g., 'back pain', 'joint pain'",
    "💡 Say: 'Mental health help' or 'stress management'",
    "💡 Ask: 'Nutrition for [condition]' or 'exercise tips'"
  ];

  const [currentHint, setCurrentHint] = useState(0);
  const [showMoreExamples, setShowMoreExamples] = useState(false);

  useEffect(() => {
    const hintInterval = setInterval(() => {
      setCurrentHint((prev) => (prev + 1) % helpfulHints.length);
    }, 3000);
    
    return () => clearInterval(hintInterval);
  }, [helpfulHints.length]);

  const handleQuickSuggestion = (suggestion) => {
    setInputMessage(suggestion);
  };

  return (
    <div className="healthcare-chatbot">
      {/* Chat Toggle Button */}
      <button 
        className={`chat-toggle-btn ${isOpen ? 'open' : ''}`}
        onClick={toggleChat}
        aria-label="Toggle healthcare chatbot"
      >
        <div className="robot-body"></div>
        {isOpen ? '' : ''}
      </button>

      {/* Chat Window */}
      <div className={`chat-window ${isOpen ? 'open' : ''}`}>
        {/* Chat Content Wrapper */}
        <div className="chat-content-wrapper">
          {/* Chat Header */}
          <div className="chat-header">
            <div className="header-content">
              <div className="bot-avatar">🩺</div>
              <div className="header-text">
                <h3>Healthcare Assistant</h3>
                <span className="status">Online • General Health Info</span>
              </div>
            </div>
            <div className="header-actions">
              <button 
                className="clear-btn" 
                onClick={clearChat}
                title="Clear conversation"
              >
                🗑️
              </button>
              <button 
                className="close-btn" 
                onClick={toggleChat}
                title="Close chat"
              >
                ✕
              </button>
            </div>
          </div>

          {/* Main Content Area - Row Layout */}
          <div className="chat-main-content">
            {/* Left Panel - Messages */}
            <div className="chat-left-panel">
              <div className="messages-container">
                {messages.length === 0 && (
                  <div className="empty-state">
                    <div className="empty-icon">🩺</div>
                    <p>Welcome to your Healthcare Assistant at the top! Start a conversation about your health concerns below.</p>
                  </div>
                )}

                {messages.map((message, index) => (
                  <div 
                    key={message.id} 
                    className={`message ${message.sender}`}
                  >
                    <div className="message-content">
                      {message.sender === 'bot' && (
                        <div className="message-avatar">🩺</div>
                      )}
                      <div className={`message-bubble ${index === 0 && message.sender === 'bot' ? 'welcome-message' : ''}`}>
                        <div className="message-text">
                          {formatMessage(message.text)}
                        </div>
                        <div className="message-time">
                          {message.timestamp.toLocaleTimeString([], { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {isLoading && (
                  <div className="message bot">
                    <div className="message-content">
                      <div className="message-avatar">🩺</div>
                      <div className="message-bubble">
                        <div className="typing-indicator">
                          <span></span>
                          <span></span>
                          <span></span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Input Area */}
              <div className="input-area">
                <div className="disclaimer-note">
                  ⚠️ For medical emergencies, call 911. This is for general health information only.
                </div>
                <div className="input-container">
                  <textarea
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Ask about symptoms, health tips, or general wellness..."
                    className="message-input"
                    rows="1"
                    disabled={isLoading}
                  />
                  <button 
                    onClick={handleSendMessage}
                    className="send-btn"
                    disabled={!inputMessage.trim() || isLoading}
                  >
                    ➤
                  </button>
                </div>
              </div>
            </div>

            {/* Right Panel - Suggestions and Hints */}
            <div className="chat-right-panel">
              {/* Quick Suggestions */}
              {messages.length <= 2 && (
                <div className="quick-suggestions">
                  <div className="suggestions-header">
                    <p>Quick suggestions:</p>
                    <button 
                      className="toggle-examples-btn"
                      onClick={() => setShowMoreExamples(!showMoreExamples)}
                    >
                      {showMoreExamples ? 'Show Less' : 'More Examples'}
                    </button>
                  </div>
                  <div className="suggestions-grid">
                    {(showMoreExamples ? [...quickSuggestions, ...moreExamples] : quickSuggestions).map((suggestion, index) => (
                      <button
                        key={index}
                        className="suggestion-btn"
                        onClick={() => handleQuickSuggestion(suggestion)}
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Helpful Hints */}
              <div className="helpful-hints">
                <div className="hint-header">
                  <span className="hint-icon">💡</span>
                  <span>How to ask me questions:</span>
                </div>
                <div className="rotating-hint">
                  {helpfulHints[currentHint]}
                </div>
                <div className="supported-topics">
                  <strong>I can help with:</strong> Symptoms, Diseases (diabetes, asthma, arthritis, heart disease, etc.), 
                  Mental health, Nutrition, Exercise, Prevention tips, and Emergency guidance.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HealthcareChatbot;
